/* eslint-disable react/no-multi-comp */
import { forwardRef, useMemo } from 'react';
import Link from 'next/link';

import clsx from 'clsx';

import { KpiService } from '@/utils/services/KpiService';

//hooks
import { KpiProvider, useKpi } from '@/hooks/context/useKpi';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useFormatKpiValue, {
    KPI_VALUE_PERCENT,
    KPI_VALUE_TIME,
    KPI_VALUE_TIME_DECIMAL
} from '@/utils/hooks/format/useFormatKpiValue';
import useFormatNumber, {
    getNumberFormat,
    percentFormat,
    percentFormatCompact
} from '@/utils/hooks/format/useFormatNumber';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useSocialProfilePermissions from '@/utils/hooks/swr/useSocialProfilePermissions';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import faqUrl from '@/utils/urls/faqUrl';

import PropTypes from 'prop-types';

//lib
import { ORIENTATION_HORIZONTAL, ORIENTATION_VERTICAL } from '@/lib/gridElements/orientation';
import { PERMISSIONS } from '@/lib/permissions/socialPermissions';
import { FACEBOOK, INSTAGRAM, TIKTOK_BUSINESS } from '@/lib/socialProfile';
import externalUrls from '@/lib/urls/externalUrl';
import { url_faq_estimated_followers } from '@/lib/urls/faqUrls';

//Components
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import Tooltip from '@/components/base/Tooltip';
import AggregatedNetworksList from '@/components/composite/Analyze/AggregatedNetworksList';
import { Trans } from '@/components/tools/translation';

export const KPI_STYLES = {
    DEFAULT: 'default',
    EXPORT: 'export',
    MEDIA_VIEWER: 'media-viewer',
    ALBUM: 'album',
    DASHBOARD: 'dashboard'
};

export const KPI_VARIANTS = {
    default: 'bg-white  rounded-md transition duration-200 hover:shadow-icoXL',
    noShadow: 'bg-white  rounded-md transition duration-200',
    orange: 'overflow-hidden flex-1 rounded bg-icoOrange-100  p-8 xxl:p-12 flex flex-col items-center justify-center',
    blue: 'overflow-hidden flex-1 rounded bg-icoBlue-100 p-8 xxl:p-12 flex flex-col items-center justify-center'
};

export const KPI_VARIANTS_DARK = {
    default: 'dark:bg-icoDarkMode-anthracite dark:hover:shadow-icoDarkXL',
    noShadow: 'dark:bg-icoDarkMode-anthracite',
    orange: 'dark:bg-icoOrange/20',
    blue: 'dark:bg-icoBlue/20'
};

const KPI = ({
    forceLightMode = false,
    data,
    config,
    userNumbersFormat,
    children,
    className,
    isLoading,
    renderLoader,
    kpiStyle,
    variant = 'default',
    ...restProps
}) => {
    if (isLoading) {
        return renderLoader();
    }
    return (
        <KpiProvider
            defaultValues={{
                data,
                config,
                userNumbersFormat,
                isLoading,
                kpiStyle,
                forceLightMode
            }}>
            <div
                id={`kpi_${config.id}`}
                className={clsx(
                    'relative flex h-full w-full flex-col items-center',
                    KPI_VARIANTS[variant],
                    className,
                    {
                        [KPI_VARIANTS_DARK[variant]]: !forceLightMode,
                        'border border-icoGray-500/30': kpiStyle === KPI_STYLES.EXPORT,
                        'text-icoBlack': forceLightMode
                    }
                )}
                {...restProps}>
                {children}
            </div>
        </KpiProvider>
    );
};

KPI.propTypes = {
    forceLightMode: PropTypes.bool,
    data: PropTypes.object,
    config: PropTypes.object.isRequired,
    userNumbersFormat: PropTypes.object,
    children: PropTypes.node,
    className: PropTypes.string,
    isLoading: PropTypes.bool,
    kpiStyle: PropTypes.oneOf(Object.values(KPI_STYLES))
};

const Wrapper = ({ children, className, ...restProps }) => {
    return (
        <div className={clsx('flex h-full w-full flex-1 items-center', className)} {...restProps}>
            {children}
        </div>
    );
};

Wrapper.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node
};

KPI.Wrapper = Wrapper;

const KpiHelpTooltip = ({ className = null }) => {
    const { kpiData } = useKpi();
    const { isLoading, config, forceLightMode } = kpiData;
    const { t } = useTranslation('analytics');
    return !isLoading && config?.tooltip ? (
        /* Add span to prevent tippy warning */
        <span className={clsx(className ?? 'absolute right-2.5 top-2 align-top leading-none')}>
            <Tooltip
                content={typeof config.tooltip === 'string' ? t(config.tooltip) : config.tooltip}
                placement="top"
                interactive={true}
                appendTo={document.body}
                popperOptions={{
                    //prevent tooltip to pop in the wrong place
                    strategy: 'fixed'
                }}>
                <Icon
                    aria-label={t('commons:infos')}
                    icon="help"
                    className={clsx('text-xs text-icoGray-400 hover:text-icoGray-500', {
                        'dark:text-icoDarkMode-wolf': !forceLightMode
                    })}
                />
            </Tooltip>
        </span>
    ) : null;
};

KpiHelpTooltip.propTypes = {
    className: PropTypes.string
};

KPI.KpiHelpTooltip = KpiHelpTooltip;

const KpiIcon = ({ IconComponent = null, className }) => {
    const { kpiData } = useKpi();
    const { config, forceLightMode } = kpiData;

    if (IconComponent) {
        return <IconComponent />;
    }

    return (
        <div className="flex items-center justify-center">
            <div
                className={clsx(
                    'flex items-center justify-center rounded-full',
                    config.icon.style,
                    className ?? 'h-17 w-17 text-2xl',
                    { 'dark:bg-icoDarkMode-deepBlack': !forceLightMode }
                )}>
                <Icon icon={config.icon.name} />
            </div>
        </div>
    );
};

KpiIcon.propTypes = {
    config: PropTypes.object,
    kpiStyle: PropTypes.oneOf(Object.values(KPI_STYLES))
};

KPI.Icon = KpiIcon;

const Content = ({ children, className }) => {
    return <div className={clsx('flex flex-col space-y-3', className)}>{children}</div>;
};

Content.propTypes = {
    config: PropTypes.object,
    data: PropTypes.object,
    userNumbersFormat: PropTypes.object,
    kpiStyle: PropTypes.oneOf(Object.values(KPI_STYLES))
};

KPI.Content = Content;

const ContentMainDataContainer = ({ children, className }) => {
    return (
        <div className={clsx('flex flex-col items-center space-y-1', className)}>{children}</div>
    );
};

KPI.ContentMainDataContainer = ContentMainDataContainer;

const MainDataWrapper = ({ children, className }) => {
    return (
        <span className={clsx('inline-flex items-center font-GBold leading-none', className)}>
            {children}
        </span>
    );
};

KPI.MainDataWrapper = MainDataWrapper;

const KpiName = ({ className }) => {
    const { t } = useTranslation('analytics');
    const { kpiData, forceLightMode } = useKpi();
    const { config } = kpiData;
    return (
        <span
            className={clsx('font-GSemiBold text-icoGray-500', className, {
                'dark:text-icoDarkMode-wolf': !forceLightMode
            })}>
            {t(config.name)}
        </span>
    );
};

KPI.KpiName = KpiName;

const networkAdsPerms = [INSTAGRAM, FACEBOOK, TIKTOK_BUSINESS];

const PromotedContent = () => {
    const { forceLightMode } = useKpi();
    const { t } = useTranslation('analytics');

    return (
        <Tooltip content={t('merged_paid_organic')} placement="top">
            <Icon
                icon="money"
                className={clsx('mr-2 text-xl text-icoBlack', {
                    'dark:text-icoDarkMode-wolf': !forceLightMode
                })}
            />
        </Tooltip>
    );
};

const Promoted = () => {
    const { kpiData } = useKpi();
    const { config } = kpiData;

    // We retrieve paid analytics even without the perm so we must always display the info
    if (config.mergedData && config?.network === TIKTOK_BUSINESS) {
        return <PromotedContent />;
    }

    if (
        (!config?.permissions?.includes(PERMISSIONS.ADS) &&
            networkAdsPerms.includes(config?.network)) ||
        !config.mergedData ||
        (!networkAdsPerms.includes(config?.network) && !config.mergedData)
    )
        return null;

    return <PromotedContent />;
};

KPI.Promoted = Promoted;

const PromotedExport = ({ exportData }) => {
    const { t } = useTranslation('analytics');
    const { kpiData } = useKpi();
    const { config, forceLightMode } = kpiData;
    const { activatedSocialProfiles } = useSocialProfiles({
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const socialProfile = useMemo(() => {
        return (
            exportData?.social_profile ||
            activatedSocialProfiles?.find((sp) => sp.id === config.social_profiles[0])
        );
    }, [activatedSocialProfiles, config.social_profiles, exportData?.social_profile]);

    const { permissions } = useSocialProfilePermissions(socialProfile.id);

    const hasPermissions = useMemo(() => {
        if (Object.keys(exportData).length > 0) {
            return exportData?.permissions?.[socialProfile.id]?.includes(PERMISSIONS.ADS);
        }

        return permissions?.includes(PERMISSIONS.ADS);
    }, [exportData, permissions, socialProfile.id]);

    // Facebook has always merged data
    if (
        (socialProfile.social_network_type === INSTAGRAM && !hasPermissions) ||
        !config.mergedData ||
        (socialProfile.social_network_type !== INSTAGRAM && !config.mergedData)
    )
        return null;

    return (
        <Tooltip content={t('merged_paid_organic')} placement="top">
            <Icon
                icon="money"
                className={clsx('mr-2 text-xl text-icoBlack', {
                    'dark:text-icoDarkMode-wolf': !forceLightMode
                })}
            />
        </Tooltip>
    );
};

KPI.PromotedExport = PromotedExport;

const MainDataWithTooltip = ({ className }) => {
    const { kpiData } = useKpi();
    const { config, data, userNumbersFormat, forceLightMode } = kpiData;
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatKpiValue } = useFormatKpiValue(config.data_format, userNumbersFormat?.number);

    if (!data?.value) {
        return (
            <span
                className={clsx(className, {
                    'text-icoGray-400': !data?.value && data?.value !== 0,
                    'dark:text-icoDarkMode-beton':
                        !data?.value && data?.value !== 0 && !forceLightMode
                })}
                aria-label={`kpi_${config.id}_value`}>
                {data?.value || data?.value === 0 ? formatKpiValue(data.value) : '-'}
            </span>
        );
    }

    return (
        <Tooltip
            content={
                data?.value || data?.value === 0
                    ? formatNumber(
                          data.value,
                          config?.data_format === KPI_VALUE_PERCENT
                              ? {
                                    ...percentFormat,
                                    maximumFractionDigits: 5
                                }
                              : {}
                      )
                    : ''
            }
            placement="top">
            <span
                className={clsx(className, {
                    'text-icoGray-400': !data?.value && data?.value !== 0,
                    'dark:text-icoDarkMode-beton':
                        !data?.value && data?.value !== 0 && !forceLightMode
                })}
                aria-label={`kpi_${config.id}_value`}>
                {data?.value || data?.value === 0 ? formatKpiValue(data.value) : '-'}
            </span>
        </Tooltip>
    );
};

KPI.MainDataWithTooltip = MainDataWithTooltip;

const MainData = ({ className }) => {
    const { kpiData } = useKpi();
    const { config, data, userNumbersFormat, forceLightMode } = kpiData;
    const { formatKpiValue } = useFormatKpiValue(config.data_format, userNumbersFormat?.number);

    return (
        <span
            className={clsx(className, {
                'text-icoGray-400 dark:text-icoDarkMode-beton':
                    !data?.value && data?.value !== 0 && !forceLightMode,
                'text-icoGray-400': !data?.value && data?.value !== 0 && forceLightMode
            })}
            aria-label={`kpi_${config.id}_value`}>
            {data?.value || data?.value === 0 ? formatKpiValue(data.value) : '-'}
        </span>
    );
};

KPI.MainData = MainData;

const SplitOrganicPayed = ({ exportData = {} }) => {
    const { t } = useTranslation('analytics');
    const { kpiData } = useKpi();
    const { config, data, userNumbersFormat, forceLightMode } = kpiData;
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const socialProfileSelected = useMemo(() => {
        return exportData?.social_profiles
            ? exportData.social_profiles.find((sp) => sp.id === config?.social_profiles[0])
            : config?.social_profiles
              ? activatedSocialProfiles?.find((sp) => sp.id === config.social_profiles[0])
              : activeSocialProfile;
    }, [
        activatedSocialProfiles,
        activeSocialProfile,
        config.social_profiles,
        exportData.social_profiles
    ]);

    const splitValues = KpiService.getSplitValues({
        data,
        config,
        socialNetworkType: socialProfileSelected?.social_network_type
    });

    if (data?.value !== null && splitValues) {
        return (
            <div
                className={clsx('flex justify-center', {
                    'justify-start': config.orientation === ORIENTATION_HORIZONTAL,
                    'justify-center': config.orientation === ORIENTATION_VERTICAL
                })}>
                <div className="inline-flex flex-col space-y-1 rounded-m bg-icoGray-100 px-2.5 py-1 dark:bg-icoDarkMode-deepBlack">
                    {activeSocialProfile?.social_network_type === FACEBOOK ? (
                        <p className="flex items-center gap-1 text-8 uppercase tracking-0.5 text-icoGray-400 dark:text-icoDarkMode-wolf">
                            <span className="leading-none">{t('analytics:estimated_data')}</span>
                            <Tooltip
                                interactive
                                content={
                                    <p className="normal-case">
                                        <Trans
                                            i18nKey="analytics:kpi.facebook.tooltip_facebook_merged_data"
                                            components={{
                                                link: (
                                                    <Link
                                                        href={
                                                            externalUrls.facebook_metrics_labeling
                                                        }
                                                        target="_blank"
                                                        className="text-icoBlue"
                                                        rel="noopener noreferrer"
                                                    />
                                                )
                                            }}
                                        />
                                    </p>
                                }>
                                <Icon icon="help" />
                            </Tooltip>
                        </p>
                    ) : null}
                    <div
                        className={clsx('flex justify-center gap-2', {
                            'lg:justify-start': config.orientation !== ORIENTATION_VERTICAL
                        })}>
                        <span
                            className={clsx(
                                'inline-flex items-center font-GSemiBold text-[0.75rem] text-icoGray-500',
                                { 'dark:text-icoDarkMode-wolf': !forceLightMode }
                            )}>
                            <Tooltip
                                content={t(
                                    splitValues.hasOrganicViralData
                                        ? 'organic_viral_data'
                                        : 'organic_data'
                                )}
                                placement="top">
                                <Icon icon="organic-data" className="mr-1" />
                            </Tooltip>
                            <span>
                                {formatNumber(
                                    splitValues.organic,
                                    getNumberFormat(config?.data_format)
                                )}
                            </span>
                        </span>
                        <span
                            className={clsx(
                                'inline-flex items-center font-GSemiBold text-[0.75rem] text-icoGray-500',
                                { 'dark:text-icoDarkMode-wolf': !forceLightMode }
                            )}>
                            <Tooltip content={t('paid_data')} placement="top">
                                <Icon icon="paid-data" className="mr-1" />
                            </Tooltip>
                            <span>
                                {formatNumber(
                                    splitValues.paid,
                                    getNumberFormat(config?.data_format)
                                )}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

KPI.SplitOrganicPayed = SplitOrganicPayed;

const DATA_EVOLUTION_VARIANTS = {
    small: 'text-sm',
    big: 'text-md mt-3'
};

const DataEvolution = forwardRef(({ className, variant = 'small' }, ref) => {
    const { kpiData } = useKpi();
    const { config, data, userNumbersFormat } = kpiData;
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);

    if (data?.evolution || data?.evolution === 0) {
        return (
            <div
                ref={ref}
                className={clsx(
                    'inline-flex items-center',
                    DATA_EVOLUTION_VARIANTS[variant],
                    className,
                    {
                        'text-icoRed':
                            (data.evolution < 0 && !config?.evolution_reverse) ||
                            (data.evolution > 0 && config?.evolution_reverse),
                        'text-icoGreen':
                            (data.evolution > 0 && !config?.evolution_reverse) ||
                            (data.evolution < 0 && config?.evolution_reverse),
                        'text-icoGray-400': data.evolution === 0,
                        'justify-center': config.orientation === ORIENTATION_VERTICAL
                    }
                )}>
                <Icon
                    className="mr-1"
                    icon={
                        data.evolution > 0
                            ? 'arrow-45-top'
                            : data.evolution < 0
                              ? 'arrow-45-bottom'
                              : 'arrow-right'
                    }
                />
                {data.evolution !== 0
                    ? formatNumber(data.evolution, {
                          ...percentFormatCompact,
                          signDisplay: 'always'
                      })
                    : formatNumber(data.evolution, {
                          ...percentFormatCompact,
                          signDisplay: 'always'
                      }).replace('+', '')}
            </div>
        );
    }
    return null;
});

DataEvolution.displayName = 'DataEvolution';
KPI.DataEvolution = DataEvolution;

const DataEvolutionWithTooltip = () => {
    const { kpiData } = useKpi();
    const { config, data, userNumbersFormat } = kpiData;
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatDateTime } = useFormatDateTime(userNumbersFormat?.date?.timezone);
    const { formatKpiValue } = useFormatKpiValue(config.data_format, userNumbersFormat?.number);

    const getTooltipValueDisplay = (value) => {
        if (
            config.data_format === KPI_VALUE_TIME ||
            config.data_format === KPI_VALUE_TIME_DECIMAL
        ) {
            return formatKpiValue(value);
        }

        if (config.data_format === KPI_VALUE_PERCENT) {
            return formatNumber(value, percentFormat);
        }

        return formatNumber(value);
    };

    if (data?.evolution || data?.evolution === 0) {
        return (
            <div
                className={clsx('flex justify-center', {
                    'lg:justify-start': config.orientation !== ORIENTATION_VERTICAL
                })}>
                {/* Add div wrapper to prevent tippy warning */}
                <Tooltip
                    interactive
                    content={
                        <Trans
                            i18nKey={
                                data.evolution < 0
                                    ? config?.growth_tooltip?.down
                                    : data.evolution > 0
                                      ? config?.growth_tooltip?.up
                                      : config?.growth_tooltip?.still
                            }
                            components={{ br: <br /> }}
                            values={{
                                current: getTooltipValueDisplay(data.value),
                                previous: getTooltipValueDisplay(data.previous),
                                start: formatDateTime(
                                    config?.start,
                                    userNumbersFormat?.date?.format
                                ),
                                end: formatDateTime(config?.end, userNumbersFormat?.date?.format)
                            }}
                        />
                    }
                    placement="top">
                    <DataEvolution />
                </Tooltip>
            </div>
        );
    }

    return null;
};

KPI.DataEvolutionWithTooltip = DataEvolutionWithTooltip;

const EstimatedNotice = () => {
    const { t } = useTranslation('analytics');
    const { kpiData } = useKpi();
    const { data, config, forceLightMode } = kpiData;

    if (data?.previous_out_of_bounds) {
        return null;
    }

    if (!data?.data_complete) {
        return (
            <p
                className={clsx(
                    '!mt-2 text-center font-GMedium text-xs text-icoGray-400 lg:text-left',
                    { 'dark:text-icoDarkMode-beton': !forceLightMode }
                )}>
                {data?.value || data?.value === 0 ? (
                    <Trans
                        i18nKey="analytics:estimated_data_kpi"
                        components={{
                            tag0: (
                                <LinkBase
                                    key="learn_more"
                                    className="underline"
                                    target="_blank"
                                    href={faqUrl(
                                        config?.learnMoreUrl ?? url_faq_estimated_followers
                                    )}
                                    rel="help noopener noreferrer"
                                />
                            )
                        }}
                    />
                ) : (
                    <Trans
                        i18nKey="analytics:no_data_estimated_kpi"
                        components={{
                            tag0: (
                                <LinkBase
                                    key="learn_more"
                                    className="underline"
                                    target="_blank"
                                    href={faqUrl(
                                        config?.learnMoreUrl ?? url_faq_estimated_followers
                                    )}
                                    rel="help noopener noreferrer"
                                />
                            )
                        }}
                    />
                )}
            </p>
        );
    }
    if (!data?.value && config?.noDataToken) {
        return (
            <p
                className={clsx(
                    '!mt-2 text-center font-GMedium text-xs text-icoGray-400 lg:text-left',
                    { 'dark:text-icoDarkMode-beton': !forceLightMode }
                )}>
                {t(config.noDataToken)}
            </p>
        );
    }

    return null;
};

KPI.EstimatedNotice = EstimatedNotice;

const AggregatedNetworks = ({ exportData = {}, className }) => {
    const { kpiData } = useKpi();
    const { config, forceLightMode } = kpiData;
    const { spGroups } = useSPGroups({
        revalidateIfStale: false
    });

    return (
        <AggregatedNetworksList
            group={exportData?.group ?? spGroups?.find((group) => group.id === config?.groups?.[0])}
            aggregatedNetworkTypes={config?.aggregatedNetworks}
            forceLightMode={forceLightMode}
            className={clsx(
                'absolute bottom-0 left-0 flex w-full flex-col items-center space-y-0.5 p-5',
                className
            )}
        />
    );
};

KPI.AggregatedNetworks = AggregatedNetworks;

const Footer = ({ children, className }) => {
    const { kpiData } = useKpi();
    const { forceLightMode } = kpiData;
    return (
        <div
            aria-label="kpi footer stats"
            className={clsx(
                'flex flex-wrap justify-center gap-1 rounded bg-icoGray-100 text-icoGray-400',
                className,
                { 'dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf': !forceLightMode }
            )}>
            {children}
        </div>
    );
};

KPI.Footer = Footer;

export default KPI;
